import type { Locale } from "../../shared/dto";
import { intlLanguage } from "../date-format";
import { translateCount } from "../i18n";
import { clientIdentityKey } from "./client-picker";

// ---- Two rows, one person, two records (F138 kolečko 3, vada 11) ----
// Pavel read the Dnes queue and found "Zkontroluj a zveřejni: Prodejní
// stránka" twice under Libuše, twice under Markéta, twice under Dagmar, and
// "Krok Trénink je zablokovaný" twice each. The fold law (fixr5) says one row
// per client, so the queue looked broken.
//
// It is not. Measured on the dev D1 (2026-08-11): each of the three women has
// TWO client records - one created 30./31. 7. under the demo partner, one
// created 6. 8. under the real one - with the SAME name and the SAME address.
// `foldByClient` folds per client and returns exactly one row for each of
// them, so both rows are true and each opens a different client.
//
// What was broken is that the console never said so. `ambiguousClientNames`
// added the address whenever two visible rows shared a NAME, on the assumption
// that an address settles it; here both records carry `pvl.riha+huna@gmail.com`,
// so the disambiguator printed the same string under both rows and the reader
// still had two identical lines pointing at two different clients.
//
// The law, one floor up: when two rows the reader can SEE point at different
// clients and render the same, the row carries a handle that really differs.
// The client picker and the import history already had one (`duplicateIndexes`,
// audit S2-20) and this is the same idea over the same data - the queue and
// the client list are simply where it never got applied.

export interface ClientIdentityRow {
  clientId: string;
  name: string;
  /** Optional because a row type may carry it optionally; "" is "no address". */
  email?: string | null;
  /** When the RECORD was created. The one fact that differs between twins. */
  createdAt?: string | null;
}

export interface ClientRowHandle {
  /** The address, whenever two visible rows share a name (the old behaviour). */
  email: string | null;
  /** Which record this is, when the address does not settle it either. */
  record: { index: number; total: number; createdAt: string | null } | null;
}

function normalized(value: string | null | undefined): string {
  return (value ?? "").trim();
}

/**
 * A handle per client id, for exactly the clients whose rows would otherwise
 * be indistinguishable. Clients that stand alone are ABSENT from the map: on
 * an unambiguous row the mark would be pure noise (the picker's own rule).
 *
 * The ordinal is per CLIENT, not per row, and it is ordered by creation, so
 * record 1 is always the original and the same client keeps the same number in
 * every stream of Dnes. Rows the reader cannot see must never enter here - the
 * handle answers "which of the two in front of me is this", so it is computed
 * over the VISIBLE rows, after every filter.
 */
export function clientRowHandles(
  rows: readonly ClientIdentityRow[]
): Map<string, ClientRowHandle> {
  const byClient = new Map<string, ClientIdentityRow>();
  for (const row of rows) if (!byClient.has(row.clientId)) byClient.set(row.clientId, row);

  const byName = new Map<string, ClientIdentityRow[]>();
  for (const row of byClient.values()) {
    const key = normalized(row.name).toLowerCase();
    const bucket = byName.get(key);
    if (bucket) bucket.push(row);
    else byName.set(key, [row]);
  }

  const handles = new Map<string, ClientRowHandle>();
  for (const sharingName of byName.values()) {
    if (sharingName.length < 2) continue;
    const byIdentity = new Map<string, ClientIdentityRow[]>();
    for (const row of sharingName) {
      const key = clientIdentityKey(normalized(row.name), normalized(row.email));
      const bucket = byIdentity.get(key);
      if (bucket) bucket.push(row);
      else byIdentity.set(key, [row]);
    }
    for (const twins of byIdentity.values()) {
      const ordered = [...twins].sort(byCreation);
      ordered.forEach((row, position) => {
        const email = normalized(row.email);
        const record = twins.length > 1
          ? { index: position + 1, total: twins.length, createdAt: row.createdAt ?? null }
          : null;
        // A handle with nothing in it would render as an empty span and say
        // less than no mark at all.
        if (!email && !record) return;
        handles.set(row.clientId, { email: email || null, record });
      });
    }
  }
  return handles;
}

/** Oldest record first; a row with no creation date sorts last, id decides ties. */
function byCreation(a: ClientIdentityRow, b: ClientIdentityRow): number {
  const left = Date.parse(a.createdAt ?? "");
  const right = Date.parse(b.createdAt ?? "");
  const leftKnown = Number.isFinite(left);
  const rightKnown = Number.isFinite(right);
  if (leftKnown && rightKnown && left !== right) return left - right;
  if (leftKnown !== rightKnown) return leftKnown ? -1 : 1;
  return a.clientId.localeCompare(b.clientId);
}

/**
 * ONLY what tells this row from its twin, for both surfaces.
 *
 * 🔴 When the record part fires, the address is IDENTICAL on both rows and
 * therefore carries nothing: printing it would be four words of noise on a
 * queue row that already names the person above it. So the address stays only
 * for the one case where it IS the difference. The DATE stays because the
 * ordinal alone cannot be checked against anything: an admin who opens record
 * 2 and comes back needs to recognise the line they were on.
 */
export function clientHandleText(
  handle: ClientRowHandle,
  t: (key: string) => string,
  locale: Locale
): string {
  if (!handle.record) return handle.email ?? "";
  const parts = [
    translateCount(locale, "staffConsole.duplicateRecord", handle.record.total)
      .replace("{index}", String(handle.record.index))
  ];
  const created = formatRecordDate(handle.record.createdAt, locale);
  if (created) parts.push(t("staffConsole.duplicateRecordCreated").replace("{date}", created));
  return parts.join(" · ");
}

/** Short and date-only: the row already carries a clock for its own age. */
function formatRecordDate(value: string | null, locale: Locale): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: "Europe/Prague"
  }).format(date);
}
